import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
  Divider,
  Stack,
  InputAdornment,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import {
  getTickets,
  createTicket,
  updateTicket,
  deleteTicket,
  getTicketDetail,
  addTicketMessage,
} from "../services/ticketService";
import { getAssignableUsers } from "../services/userService";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PRIORITIES = ["low", "medium", "high", "urgent"];
const STATUSES = ["open", "in_progress", "resolved", "closed"];

export default function SupportTicketsPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState("");

  // Create ticket modal
  const [openCreate, setOpenCreate] = useState(false);
  const [issue, setIssue] = useState("");
  const [priority, setPriority] = useState("medium");

  // Respond modal
  const [openRespond, setOpenRespond] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");

  // Assign modal
  const [openAssign, setOpenAssign] = useState(false);
  const [assignTicketId, setAssignTicketId] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");

  const load = async () => {
    try {
      setError("");
      setLoading(true);
      const data = await getTickets();
      setTickets(data);
    } catch (e) {
      setError(e.message || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = useMemo(() => {
    return [...tickets].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
  }, [tickets]);

  const handleCreate = async () => {
    try {
      await createTicket({ issue, priority });
      setOpenCreate(false);
      setIssue("");
      setPriority("medium");
      await load();
    } catch (e) {
      setError(e.message || "Failed to create ticket");
    }
  };

  const openTicket = async (t) => {
    try {
      setError("");
      setActiveTicket(t);
      setOpenRespond(true);
      setReply("");

      const detail = await getTicketDetail(t.id);
      setMessages(detail.messages || []);
    } catch (e) {
      setError(e.message || "Failed to load ticket details");
    }
  };

  const handleSendReply = async () => {
    if (!activeTicket) return;
    try {
      await addTicketMessage(activeTicket.id, { message: reply });

      const detail = await getTicketDetail(activeTicket.id);
      setMessages(detail.messages || []);
      setReply("");
      await load();
    } catch (e) {
      setError(e.message || "Failed to send response");
    }
  };

  const handleAdminUpdate = async (ticketId, patch) => {
    try {
      await updateTicket(ticketId, patch);
      await load();
    } catch (e) {
      setError(e.message || "Update failed");
    }
  };

  const handleDelete = async (ticketId) => {
    try {
      await deleteTicket(ticketId);
      await load();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  const openAssignModal = async (ticketId) => {
    try {
      setError("");
      setAssignTicketId(ticketId);
      setOpenAssign(true);
      setUserSearch("");

      // Load users only once per open (or cache)
      setUsersLoading(true);
      const list = await getAssignableUsers();
      setUsers(list);
    } catch (e) {
      setError(e.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;

    return users.filter((u) => {
      const name = (u.name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const role = (u.role || "").toLowerCase();
      return name.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [users, userSearch]);

  const assignToUser = async (userId) => {
    if (!assignTicketId) return;
    try {
      await updateTicket(assignTicketId, { assigned_to: userId });
      setOpenAssign(false);
      setAssignTicketId(null);
      await load();
    } catch (e) {
      setError(e.message || "Failed to assign ticket");
    }
  };

  const unassign = async (ticketId) => {
    try {
      await updateTicket(ticketId, { assigned_to: null });
      await load();
    } catch (e) {
      setError(e.message || "Failed to unassign");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Support Tickets
        </Typography>

        <Button
          variant="contained"
          size="small"
          onClick={() => setOpenCreate(true)}
        >
          Raise Ticket
        </Button>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: "error.main", fontSize: 13 }}>
            {error}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: "1px solid rgba(0,0,0,0.10)",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ color: "text.secondary" }}>
              Loading tickets...
            </Typography>
          </Box>
        ) : sorted.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ color: "text.secondary" }}>
              No tickets yet.
            </Typography>
          </Box>
        ) : (
          sorted.map((t, idx) => (
            <Box key={t.id}>
              <Box
                sx={{ p: 2, display: "flex", gap: 2, alignItems: "flex-start" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip size="small" label={t.priority} />
                    <Chip size="small" variant="outlined" label={t.status} />
                    <Typography sx={{ fontWeight: 800 }}>{t.issue}</Typography>
                  </Box>

                  <Typography
                    sx={{ mt: 0.6, fontSize: 13, color: "text.secondary" }}
                  >
                    Raised by:{" "}
                    {t.raised_by_name || t.raised_by_email || "—"} •{" "}
                    {fmtDate(t.created_at)}
                  </Typography>

                  <Typography
                    sx={{ mt: 0.2, fontSize: 13, color: "text.secondary" }}
                  >
                    Assigned to: {t.assigned_to_name || "—"}
                  </Typography>

                  {isAdmin && (
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mt: 1.25, flexWrap: "wrap" }}
                    >
                      <TextField
                        select
                        size="small"
                        label="Priority"
                        value={t.priority}
                        onChange={(e) =>
                          handleAdminUpdate(t.id, { priority: e.target.value })
                        }
                        sx={{ minWidth: 140 }}
                      >
                        {PRIORITIES.map((p) => (
                          <MenuItem key={p} value={p}>
                            {p}
                          </MenuItem>
                        ))}
                      </TextField>

                      <TextField
                        select
                        size="small"
                        label="Status"
                        value={t.status}
                        onChange={(e) =>
                          handleAdminUpdate(t.id, { status: e.target.value })
                        }
                        sx={{ minWidth: 160 }}
                      >
                        {STATUSES.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openAssignModal(t.id)}
                        sx={{ fontWeight: 800 }}
                      >
                        Assign
                      </Button>

                      {t.assigned_to && (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => unassign(t.id)}
                          sx={{ fontWeight: 800 }}
                        >
                          Unassign
                        </Button>
                      )}

                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDelete(t.id)}
                        sx={{ fontWeight: 800 }}
                      >
                        Delete
                      </Button>
                    </Stack>
                  )}
                </Box>

                <Button
                  size="small"
                  onClick={() => openTicket(t)}
                  sx={{ fontWeight: 900 }}
                >
                  Respond
                </Button>
              </Box>

              {idx !== sorted.length - 1 && <Divider />}
            </Box>
          ))
        )}
      </Box>

      {/* Create Ticket Dialog */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Raise Support Ticket</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Issue"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            sx={{ mt: 1.5 }}
          />
          <TextField
            select
            fullWidth
            label="Priority"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            sx={{ mt: 2 }}
          >
            {PRIORITIES.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!issue.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User Dialog */}
      <Dialog
        open={openAssign}
        onClose={() => setOpenAssign(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Ticket</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            placeholder="Search name / email / role..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">🔎</InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              mt: 2,
              border: "1px solid rgba(0,0,0,0.10)",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {usersLoading ? (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ color: "text.secondary" }}>
                  Loading users...
                </Typography>
              </Box>
            ) : filteredUsers.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ color: "text.secondary" }}>
                  No users found.
                </Typography>
              </Box>
            ) : (
              filteredUsers.map((u, idx) => (
                <Box key={u.id}>
                  <Box
                    sx={{
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }} noWrap>
                        {u.name || "Unnamed User"}
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12.5, color: "text.secondary" }}
                        noWrap
                      >
                        {u.email} • {u.role}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => assignToUser(u.id)}
                      sx={{ fontWeight: 800 }}
                    >
                      Select
                    </Button>
                  </Box>
                  {idx !== filteredUsers.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssign(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Respond Dialog (unchanged) */}
      <Dialog
        open={openRespond}
        onClose={() => setOpenRespond(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Ticket</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography sx={{ fontWeight: 800 }}>
            {activeTicket?.issue || "—"}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            Raised by:{" "}
            {activeTicket?.raised_by_name || activeTicket?.raised_by_email || "—"}{" "}
            • {fmtDate(activeTicket?.created_at)}
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(0,0,0,0.10)",
            }}
          >
            {messages.length === 0 ? (
              <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
                No responses yet.
              </Typography>
            ) : (
              messages.map((m) => (
                <Box key={m.id} sx={{ mb: 1.25 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                    {m.sender_name || m.sender_email || "User"} •{" "}
                    <span
                      style={{ fontWeight: 500, color: "rgba(0,0,0,0.6)" }}
                    >
                      {fmtDate(m.created_at)}
                    </span>
                  </Typography>
                  <Typography sx={{ fontSize: 13 }}>{m.message}</Typography>
                </Box>
              ))
            )}
          </Box>

          {isAdmin && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Response"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
              />
            </Box>
          )}

          {!isAdmin && (
            <Typography sx={{ mt: 2, fontSize: 13, color: "text.secondary" }}>
              Only admins can respond in this MVP.
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenRespond(false)}>Close</Button>
          {isAdmin && (
            <Button
              variant="contained"
              onClick={handleSendReply}
              disabled={!reply.trim()}
            >
              Send
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}